import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getCustomers, getUsage } from '../api';
import api from '../api';

const DB_COLORS = [
  '#378ADD','#1D9E75','#D85A30','#7F77DD','#E6A817',
  '#C45C8A','#4AAFB8','#8B6F47','#5C9E3A','#A05CD6',
];

export default function UsageDetail({ dimension, activeOnly }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected]   = useState('');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const handleExportCsv = () => {
    const url = `/api/v1/customers/export/csv?cust_code=${encodeURIComponent(selected)}`;
    const filename = `${selected.toUpperCase()}_sample_test_counts.csv`;
    api.get(url, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(e => setError(`CSV export failed: ${e.message}`));
  };

  useEffect(() => {
    getCustomers(activeOnly).then(r => {
      setCustomers(r.data);
      setSelected('');
      setData(null);
    });
  }, [activeOnly]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    getUsage(selected, dimension)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selected, dimension]);

  // Derive unique database names in stable order
  const databases = data ? [...new Set((data.rows || []).map(r => r.database_name))] : [];

  // Pivot rows: one entry per unique month, each db as its own key
  const buildChart = (field) => {
    const monthMap = {};
    (data?.rows || []).forEach(r => {
      const month = new Date(r.log_month).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      if (!monthMap[month]) monthMap[month] = { month };
      monthMap[month][r.database_name] = (monthMap[month][r.database_name] || 0) + (r[field] || 0);
    });
    return Object.values(monthMap);
  };

  const samplesChart = buildChart('order_count');
  const testsChart   = buildChart('ord_task_count');

  return (
    <>
      <div style={{display:'flex', gap:12, alignItems:'center', marginBottom:16}}>
        <select
          style={{fontSize:13, padding:'7px 12px', border:'1px solid #e5e5e5', borderRadius:6, minWidth:280}}
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="">Select a customer...</option>
          {[...customers].sort((a,b) => a.cust_name.localeCompare(b.cust_name)).map(c => (
            <option key={c.cust_code} value={c.cust_code}>{c.cust_name}</option>
          ))}
        </select>
        {data && !loading && (
          <button
            onClick={handleExportCsv}
            style={{padding:'7px 16px', fontSize:13, borderRadius:6, border:'1px solid #e5e5e5', background:'#fff', cursor:'pointer', whiteSpace:'nowrap'}}
          >
            ↓ Export Sample Test Counts
          </button>
        )}
      </div>

      {!selected && <div className="loading">Select a customer to view usage detail</div>}
      {loading   && <div className="loading">Loading...</div>}
      {error     && <div className="error">Error: {error}</div>}

      {data && !loading && (
        <>
          <div className="metrics">
            <div className="metric">
              <div className="metric-label">Total samples</div>
              <div className="metric-value">{(data.total_orders || 0).toLocaleString()}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Total tests</div>
              <div className="metric-value">{(data.total_tasks || 0).toLocaleString()}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Months of data</div>
              <div className="metric-value">{data.month_count || 0}</div>
              <div className="metric-sub">{data.date_from} → {data.date_to}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Databases</div>
              <div className="metric-value">{databases.length}</div>
            </div>
          </div>

          <div className="two-col">
            {/* Samples by database */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">{data.customer?.cust_name} — samples by database</div>
                <div className="legend">
                  {databases.map((db, i) => (
                    <span key={db}>
                      <span className="legend-dot" style={{background: DB_COLORS[i % DB_COLORS.length]}}></span>
                      {db}
                    </span>
                  ))}
                </div>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={samplesChart} margin={{top:4, right:4, left:0, bottom:4}}>
                    <XAxis dataKey="month" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v, name) => [v.toLocaleString(), name]} />
                    {databases.map((db, i) => (
                      <Bar key={db} dataKey={db} name={db} stackId="samples"
                        fill={DB_COLORS[i % DB_COLORS.length]}
                        radius={i === databases.length - 1 ? [2,2,0,0] : [0,0,0,0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tests by database */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">{data.customer?.cust_name} — tests by database</div>
                <div className="legend">
                  {databases.map((db, i) => (
                    <span key={db}>
                      <span className="legend-dot" style={{background: DB_COLORS[i % DB_COLORS.length]}}></span>
                      {db}
                    </span>
                  ))}
                </div>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={testsChart} margin={{top:4, right:4, left:0, bottom:4}}>
                    <XAxis dataKey="month" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v, name) => [v.toLocaleString(), name]} />
                    {databases.map((db, i) => (
                      <Bar key={db} dataKey={db} name={db} stackId="tests"
                        fill={DB_COLORS[i % DB_COLORS.length]}
                        radius={i === databases.length - 1 ? [2,2,0,0] : [0,0,0,0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><div className="card-title">Monthly data</div></div>
            <table>
              <thead>
                <tr><th>Month</th><th>Database</th><th>Samples</th><th>Tests</th></tr>
              </thead>
              <tbody>
                {[...data.rows].reverse().map((r, i) => (
                  <tr key={i}>
                    <td>{new Date(r.log_month).toLocaleDateString('en-GB', {month:'long', year:'numeric'})}</td>
                    <td style={{color:'#666', fontSize:12}}>
                      <span style={{
                        display:'inline-block', width:8, height:8, borderRadius:'50%', marginRight:6,
                        background: DB_COLORS[databases.indexOf(r.database_name) % DB_COLORS.length]
                      }}></span>
                      {r.database_name}
                    </td>
                    <td>{(r.order_count    || 0).toLocaleString()}</td>
                    <td>{(r.ord_task_count || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
